import { defineStore } from 'pinia'
import axios from '@/services'

export const useWarehousesStore = defineStore('warehouses', {
  modelName: 'Warehouse',
  state: () => ({
    items: [],
    totalItems: 0,
    currentPage: 1,
    perPage: 10,
    searchQuery: '',
    loading: false,
    error: null,
    // Datos específicos
    warehouse: null,
    branchoffices: []
  }),

  getters: {
    // Getters simples que retornan el estado directamente
    getItems: (state) => state.items,
    getTotalItems: (state) => state.totalItems,
    getCurrentPage: (state) => state.currentPage,
    getPerPage: (state) => state.perPage,
    getSearchQuery: (state) => state.searchQuery,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
    getWarehouse: (state) => state.warehouse,
    getBranchoffices: (state) => state.branchoffices,
    
    // Getters computados
    hasItems: (state) => state.items.length > 0,
    totalPages: (state) => Math.ceil(state.totalItems / state.perPage)
  },

  actions: {
    onModelChanged() {
      this.fetchWarehouses()
    },

    // Obtener lista de warehouses con paginación
    async fetchWarehouses() {
      this.loading = true
      this.error = null
      
      try {
        const response = await axios.get('/api/warehouses/list', {
          params: {
            page: this.currentPage,
            per_page: this.perPage,
            search: this.searchQuery
          }
        })
        
        this.items = response.data.data
        this.totalItems = response.data.total
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar depósitos'
        console.error('Error fetching warehouses:', error)
      } finally {
        this.loading = false
      }
    },

    // Obtener todos los warehouses sin paginación (para selects)
    async fetchAllWarehouses() {
      try {
        const response = await axios.get('/api/warehouses/list', {
          params: {
            per_page: 9999, // Obtener todos los registros
            page: 1
          }
        })
        this.items = response.data.data
        return response.data.data
      } catch (error) {
        console.error('Error fetching all warehouses:', error)
        throw error
      }
    },

    // Obtener un warehouse específico
    async fetchWarehouse(id) {
      this.loading = true
      
      try {
        const response = await axios.get(`/api/warehouses/getWarehouse/${id}`)
        this.warehouse = response.data.data
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar depósito'
        throw error
      } finally {
        this.loading = false
      }
    },

    // Crear nuevo warehouse
    async createWarehouse(warehouseData) {
      const response = await axios.post('/api/warehouses/add', warehouseData)
      
      if (response.data.success) {
        // Recargar la lista
        await this.fetchWarehouses()
        return response.data
      }
      throw new Error(response.data.message || 'Error al crear depósito')
    },

    // Actualizar warehouse
    async updateWarehouse({ id, data }) {
      const response = await axios.post(`/api/warehouses/edit/${id}`, data)
      
      if (response.data.success) {
        // Recargar la lista
        await this.fetchWarehouses()
        return response.data
      }
      throw new Error(response.data.message || 'Error al actualizar depósito')
    },

    // Eliminar warehouse
    async deleteWarehouse(id) {
      const response = await axios.post('/api/warehouses/delete', { id })

      if (response.data.success) {
        // Recargar la lista
        await this.fetchWarehouses()
        return response.data
      }
      throw new Error(response.data.message || 'Error al eliminar depósito')
    },

    // Eliminar warehouse (endpoint legacy de configs)
    async removeWarehouse(warehouseData) {
      const response = await axios.post('/api/configs/warehouse/remove', warehouseData)

      if (response.status === 200) {
        // Recargar la lista si está disponible
        if (this.items.length > 0) {
          await this.fetchWarehouses()
        }
        return response.data
      }
      throw new Error('Error al eliminar depósito')
    },

    // Obtener productos de un warehouse
    async fetchWarehouseProducts(warehouseId) {
      try {
        const response = await axios.get(`/api/warehouses/products/${warehouseId}`)
        return response.data.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al cargar productos'
        throw error
      }
    },

    // Obtener sucursales
    async fetchBranchoffices() {
      try {
        const response = await axios.get('/api/branchoffices/getBranchoffices')
        this.branchoffices = response.data.data
        return response.data.data
      } catch (error) {
        console.error('Error fetching branchoffices:', error)
        throw error
      }
    },

    // Cambiar página
    setCurrentPage(page) {
      this.currentPage = page
      this.fetchWarehouses()
    },

    // Cambiar búsqueda
    setSearchQuery(query) {
      this.searchQuery = query
      this.currentPage = 1 // Reset a primera página al buscar
      this.fetchWarehouses()
    },

    // Limpiar warehouse seleccionado
    clearWarehouse() {
      this.warehouse = null
    },

    // Métodos legacy para el endpoint /api/configs/warehouse/*
    async saveWarehouse(warehouseData, isEdit = false, warehouseId = null) {
      this.loading = true
      this.error = null

      try {
        const endpoint = isEdit
          ? `/api/configs/warehouse/update/${warehouseId}`
          : '/api/configs/warehouse/add'

        const response = await axios.post(endpoint, warehouseData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        return response.data
      } catch (error) {
        this.error = error.response?.data?.message || 'Error al guardar la sucursal'
        console.error('Error saving warehouse:', error)
        throw error
      } finally {
        this.loading = false
      }
    }
  }
})